<script setup lang="ts">
import {
  computed, ComputedRef, onMounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/modules/profile/stores/user.ts';
import Background from '@/shared/components/base/Background/Background.vue';
import {
  BackgroundType, EmptyBackground, ImageBackground,
} from '@/shared/components/base/Background/types.ts';
import { logAnalyticsEvent } from '@/core/analytics/analytics';
import { AnalyticsEventName } from '@/core/analytics/types';

const { t } = useI18n();

const { user } = storeToRefs(useUserStore());

const images = [
  '/images/hello-screen/Jesus-encourages.jpg',
  '/images/hello-screen/Noah-builds.jpg',
  '/images/hello-screen/Abraham-looks.jpg',
  '/images/hello-screen/Jesus-feeds_1.jpg',
  '/images/hello-screen/Jesus-calms.jpg',
  '/images/hello-screen/David_throws.jpg',
  '/images/hello-screen/Ruth_gathers.jpg',
  '/images/hello-screen/Moses_at_the burning_bush.jpg',
  '/images/hello-screen/Esther_before King_Xerxes.jpg',
  '/images/hello-screen/Jesus_comforts Martha_and_Mary.jpg',
  '/images/hello-screen/Joseph before Pharaoh.jpg',
  '/images/hello-screen/Hannah prays in the quiet of the temple.jpg',
  '/images/hello-screen/The_risen_Christ meets_the women.jpg',
  '/images/hello-screen/Gideon with a small army triumphs.jpg',
  '/images/hello-screen/The_disciples_at Pentecost_in_the upper_room.jpg',
  '/images/hello-screen/Psalmist_David with_a_harp.jpg',
  '/images/hello-screen/Zacchaeus_on_a tree_and_Jesus.jpg',
  '/images/hello-screen/Jonah_in_the_belly_of_the_whale.jpg',
  '/images/hello-screen/Parable_of_the Prodigal_Son.jpg',
  '/images/hello-screen/Jesus_says_I_am_the_Light.jpg',
];

const savedVariant = JSON.parse(localStorage.getItem('hello-loader-screen-data') || '{}');

const getRandomIntInclusive = (min: number, max: number): number => {
  min = Math.ceil(min);
  max = Math.floor(max);

  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const savedDateDay = computed(() => {
  if (!savedVariant?.date || !savedVariant?.variant) {
    return '';
  }

  const savedDate = new Date(savedVariant.date);

  return `${savedDate.getFullYear()}-${savedDate.getMonth()}-${savedDate.getDate()}`;
});

const getVariant = () => {
  const currentDate = new Date();
  const currentDateDay = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`;

  if (savedVariant?.date && savedVariant?.variant && savedDateDay.value === currentDateDay) {
    return Number(savedVariant.variant);
  } else {
    const number = getRandomIntInclusive(1, 20);

    localStorage.setItem('hello-loader-screen-data', JSON.stringify({
      date: Date.now(),
      variant: number.toString(),
    }));

    return number;
  }
};

const variant = ref(getVariant());

const backgroundImage: ComputedRef<ImageBackground | EmptyBackground> = computed(() => {
  if (!variant.value) {
    return {
      type: BackgroundType.Empty,
    };
  }

  return {
    type: BackgroundType.Image,
    image: {
      url: new URL(images[variant.value - 1], window.location.origin).href,
    },
  };
});

const logDisplayedScreenToAnalytics = async () => {
  await logAnalyticsEvent({
    name: AnalyticsEventName.ScreenView,
    params: {
      screenName: 'hello_screen',
    },
  });
};

onMounted(async () => {
  await logDisplayedScreenToAnalytics();
});

</script>

<template>
  <Background
    :active="true"
    :background="backgroundImage"
  >
    <div class="scatic-screen-container">
      <div class="logo">
        <Icon
          name="logo-white"
          class="logo-icon"
        />
      </div>
      <div class="content">
        <div
          class="hello-text"
          v-html="t(`hello_loader_screen.hello.${getRandomIntInclusive(1, 16)}`, { name: user?.firstName || 'friend' })"
        />
        <div class="main-text">
          {{ t(`hello_loader_screen.text.${variant}`) }}
        </div>
      </div>
    </div>
    <div class="bg-gradient" />
  </Background>
</template>

<style scoped>
.scatic-screen-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 30;

  padding-left: 28px;
  padding-right: 28px;
  padding-top: var(--content-conteiner-top-padding);
  padding-bottom: calc(var(--content-conteiner-bottom-padding) + 30px);
}

.logo {
  position: absolute;
  top: var(--content-conteiner-top-padding);
  left: 28px;
}

.logo-icon {
  width: 55px;
  height: auto;
}

.content {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  width: 100%;
  height: 100%;
}

.hello-text {
  color: #C7C7C7;
  font-family: 'Poppins';
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 134%; /* 20.103px */

  margin-bottom: 12px;
}

.main-text {
  color: #FFF;
  text-shadow: 0 4px 40px rgba(0, 0, 0, 0.20);
  font-family: "Songti TC";
  font-size: 32px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%; /* 38.4px */
}

.bg-gradient {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.40) 100%);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 29;
}

</style>

<style>
.hello-screen-user-name {
  color: #FFB03C;
}
</style>
